/**
 * Card renderers for declarative related-entity sections
 * (see `relatedSections.ts` / `related-entity-section.tsx`).
 *
 * Cards are keyed by string (`EntityCardKey`) so section configs stay pure and
 * serializable. Each card renders a single related entity row and opens the
 * entity in the slideout stack on click.
 */

import { ChevronDown } from '@untitledui/icons';
import type { CSSProperties, ReactNode } from 'react';
import { memo, useCallback, useMemo } from 'react';

import {
  AccordionItem,
  AccordionPanel,
  AccordionTrigger,
} from '@/lib/base/accordion';
import type { BadgeColors } from '@/lib/base/badges/badge-types';
import { Badge } from '@/lib/base/badges/badges';
import { Button } from '@/lib/base/buttons/button';
import { CollapsibleContent } from '@/lib/base/collapsible-content';
import { MarkdownContent } from '@/lib/crm/components/MarkdownContent';
import { RiskCardContent } from '@/lib/crm/object-cards';
import { useOrgTimeFormat } from '@/lib/time';
import { getEventColor } from '@/pages/calendar/utils/calendarColors';
import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';
import { cx } from '@/utils/cx';

import { NotOnCalendarBadge } from '../../components/NotOnCalendarBadge';
import { isAllowedUrl } from '../../utils/urlValidation';
import { MeetingAttendeesById } from '../sections/meeting-attendees';
import { formatUpcomingMeetingDateTime } from '../sections/upcoming-meetings-utils';
import { isMeetingAdHoc } from '../utils/meetingHelpers';

/** Maps a meeting's `ahoy_status` value to the status badge color. */
const STATUS_BADGE_COLORS: Record<string, BadgeColors> = {
  confirmed: 'success',
  tentative: 'warning',
  cancelled: 'error',
};

/**
 * Height budget for the collapsed meeting-note summary preview. 60px is three
 * 20px lines (see the `leading-5` override below), of which CollapsibleContent's
 * hard-coded 24px bottom fade softens the last — so roughly two crisply readable
 * lines, the density the two-line-clamped paragraph this replaced had. Matches the
 * other toggle-less clamps in the app (NoteCardContent = 60, timeline events = 63).
 */
const NOTE_SUMMARY_PREVIEW_MAX_HEIGHT_PX = 60;

/**
 * Character cap applied to the summary BEFORE it is rendered. CollapsibleContent
 * clips visually, not structurally, so without this a huge summary would build
 * its entire DOM behind a 60px window — once per card, and the section holds up
 * to EXPANDED_SECTION_LIMIT (100) cards after "View N more". The cap sits far
 * beyond the ~150 characters the clamp can actually show, so it never changes
 * visible output: a cut landing mid-syntax is always below the fold. Same hazard
 * and same remedy as NoteCardContent.tsx's `excerpt` pass.
 */
const NOTE_SUMMARY_PREVIEW_CHAR_CAP = 600;

/**
 * Markdown overrides for the preview: no block margins (the height budget is for
 * text, not whitespace), headings dropped to the preview's own text-xs so they
 * don't read as a second card title, and every node inheriting the muted
 * tertiary color. The color rule is deliberately broad — it also flattens
 * syntax-highlight and alert colors inside the preview.
 */
const NOTE_SUMMARY_PREVIEW_MARKDOWN_CLASS =
  'break-words max-w-none [&_p]:!my-0 [&_p]:!leading-5 ' +
  '[&_h1]:!text-xs [&_h2]:!text-xs [&_h3]:!text-xs ' +
  '[&_h1]:!mb-0.5 [&_h2]:!mb-0.5 [&_h3]:!mb-0.5 ' +
  '[&_ul]:!my-0 [&_ol]:!my-0 [&_table]:!my-0 [&_pre]:!my-0 [&_blockquote]:!my-0 ' +
  '[&_*]:!text-inherit';

/** Props every entity card receives. */
export interface EntityCardProps {
  /** The related entity to render (e.g. a meeting or meeting-notes entity). */
  object: CrmObject;
  /** Open the entity in the slideout stack. */
  onOpen: (entityId: string) => void;
  /**
   * Response-level lookup of hydrated associated entities (from the section's
   * `includeAssociations`), keyed by entity id. Cards resolve a row's
   * association targets (e.g. a meeting's company, a note's meeting) here — the
   * list endpoint returns these at the response level, not on each row.
   */
  associatedObjects?: Record<string, CrmObject>;
}

/**
 * Display data shared by both meeting cards: the title/time/company fields, the
 * accent-color CSS variable, and the click handler. Extracted into one hook so
 * the compact `MeetingCard` and the detailed `MeetingDetailCard` read the same
 * properties the same way and can't silently drift apart.
 */
function useMeetingRowData({
  object,
  onOpen,
  associatedObjects,
}: EntityCardProps) {
  const entityId = object.entity_id;
  const title =
    getPropertyValue<string>(object.properties?.ahoy_title) ||
    'Untitled meeting';
  const startTime =
    getPropertyValue<string>(object.properties?.ahoy_start_time) ?? undefined;
  const colorId =
    getPropertyValue<string>(object.properties?.ahoy_color) ?? undefined;
  const accentColor = getEventColor(colorId);
  const isAdHoc = isMeetingAdHoc(object);

  const companyName = useMemo(() => {
    for (const assoc of object.associations ?? []) {
      if (assoc.association_type_name !== 'ahoy_meeting_to_company') {
        continue;
      }
      const companyObj = associatedObjects?.[assoc.target_entity_id];
      const name =
        companyObj &&
        getPropertyValue<string>(companyObj.properties?.ahoy_name);
      if (name) {
        return name;
      }
    }
    return undefined;
  }, [object.associations, associatedObjects]);

  const handleClick = useCallback(() => onOpen(entityId), [onOpen, entityId]);
  const accentStyle = useMemo(
    () => ({ '--meeting-color': accentColor }) as CSSProperties,
    [accentColor]
  );

  return { title, startTime, companyName, accentStyle, handleClick, isAdHoc };
}

/**
 * The title + optional company badge + optional trailing badge + start time row
 * shared by both cards. `trailing` is an optional status pill (e.g. a
 * "Summary"/"Prep" tag) rendered just before the time.
 */
function MeetingTitleRow({
  title,
  companyName,
  startTime,
  trailing,
  adHoc,
}: {
  title: string;
  companyName?: string;
  startTime?: string;
  trailing?: ReactNode;
  adHoc?: boolean;
}) {
  const { timeFormat } = useOrgTimeFormat();
  return (
    <div className='flex items-center gap-3 w-full min-w-0'>
      <span className='text-sm text-[var(--color-text-primary)] truncate min-w-0 flex-1 text-left'>
        {title}
      </span>
      {companyName && (
        <Badge type='pill-color' size='sm' color='brand'>
          {companyName}
        </Badge>
      )}
      {adHoc && <NotOnCalendarBadge />}
      {trailing}
      {startTime && (
        <span className='text-sm font-semibold text-[var(--color-text-primary)] shrink-0 tabular-nums'>
          {formatUpcomingMeetingDateTime(startTime, timeFormat)}
        </span>
      )}
    </div>
  );
}

/**
 * The clickable accent-bar shell shared by both cards: an accent stripe next to
 * a padded content column. `className` tunes the button chrome (border/rounding)
 * per variant; `children` are the content rows rendered inside the column.
 */
function MeetingCardShell({
  accentStyle,
  onClick,
  className,
  children,
}: {
  accentStyle: CSSProperties;
  onClick: () => void;
  className?: string;
  children: ReactNode;
}) {
  return (
    <Button
      color='tertiary'
      noTextPadding
      onClick={onClick}
      style={accentStyle}
      className={cx(
        'w-full overflow-hidden !justify-start !p-0 hover:!bg-[var(--color-bg-secondary)]',
        className
      )}
    >
      <div className='flex items-stretch w-full overflow-hidden'>
        <div className='w-1.5 self-stretch shrink-0 bg-[var(--meeting-color)]' />
        <div className='flex flex-col gap-2 py-3 pr-4 pl-3 flex-1 min-w-0 text-left'>
          {children}
        </div>
      </div>
    </Button>
  );
}

/**
 * Meeting row: accent bar + title + optional company badge + start time.
 * Company name is read from the row's hydrated `ahoy_meeting_to_company`
 * association (populated via the section's `includeAssociations`).
 */
function MeetingCardImpl(
  props: EntityCardProps & {
    trailing?: ReactNode;
    /** Render the company badge (default true). Set false to show only `trailing`. */
    showCompany?: boolean;
  }
) {
  const { title, startTime, companyName, accentStyle, handleClick, isAdHoc } =
    useMeetingRowData(props);
  const showCompany = props.showCompany ?? true;

  return (
    <MeetingCardShell
      accentStyle={accentStyle}
      onClick={handleClick}
      className='!rounded-[4px] border border-[var(--color-border-secondary)]'
    >
      <MeetingTitleRow
        title={title}
        companyName={showCompany ? companyName : undefined}
        startTime={startTime}
        trailing={props.trailing}
        adHoc={isAdHoc}
      />
    </MeetingCardShell>
  );
}

/**
 * Detail-oriented meeting row for a booking's single linked meeting: title +
 * optional company badge + start time (as `MeetingCardImpl`), plus a status
 * badge, attendee count, and a "Join meeting" link — the details a booking's
 * single linked meeting warrants that the compact list-style `MeetingCardImpl`
 * (shared with "Upcoming meetings") omits for space.
 */
function MeetingDetailCardImpl(props: EntityCardProps) {
  const { object } = props;
  const { title, startTime, companyName, accentStyle, handleClick, isAdHoc } =
    useMeetingRowData(props);
  const status =
    getPropertyValue<string>(object.properties?.ahoy_status) ?? undefined;
  const meetingLink =
    getPropertyValue<string>(object.properties?.ahoy_meeting_link) ?? undefined;
  const invitations = getPropertyValue<Array<Record<string, unknown>>>(
    object.properties?.ahoy_invitations
  );

  const attendeeCount = Array.isArray(invitations) ? invitations.length : null;

  const joinUrl = useMemo(
    () => (meetingLink && isAllowedUrl(meetingLink) ? meetingLink : null),
    [meetingLink]
  );

  const statusLabel = status
    ? status.charAt(0).toUpperCase() + status.slice(1)
    : undefined;
  const statusColor: BadgeColors = STATUS_BADGE_COLORS[status ?? ''] ?? 'gray';

  return (
    <div className='w-full overflow-hidden rounded-[4px] border border-[var(--color-border-secondary)]'>
      <MeetingCardShell
        accentStyle={accentStyle}
        onClick={handleClick}
        className='!rounded-none'
      >
        {(statusLabel || attendeeCount !== null) && (
          <div className='flex items-center gap-2'>
            {statusLabel && (
              <Badge type='pill-color' size='sm' color={statusColor}>
                {statusLabel}
              </Badge>
            )}
            {attendeeCount !== null && (
              <span className='text-xs text-[var(--color-text-secondary)]'>
                {attendeeCount} attendee{attendeeCount !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        )}
        <MeetingTitleRow
          title={title}
          companyName={companyName}
          startTime={startTime}
          adHoc={isAdHoc}
        />
      </MeetingCardShell>
      {joinUrl && (
        <div className='border-t border-[var(--color-border-secondary)] px-3 py-2'>
          <a
            href={joinUrl}
            target='_blank'
            rel='noopener noreferrer'
            aria-label='Join meeting (opens in new window)'
            className='text-xs md:text-sm text-[var(--color-text-primary)] underline hover:no-underline'
          >
            Join meeting
          </a>
        </div>
      )}
    </div>
  );
}

/**
 * Meeting-notes row shown OUTSIDE the meeting slideout (e.g. on a contact/deal).
 * Surfaces the originating meeting's name + time and links back to that meeting,
 * with a short summary preview. Falls back to the note's own title/date and a
 * link to the note when the meeting isn't hydrated.
 */
function MeetingNotesCardImpl({
  object,
  onOpen,
  associatedObjects,
}: EntityCardProps) {
  const { timeFormat } = useOrgTimeFormat();
  // The originating meeting, hydrated via the section's `includeAssociations`
  // on `ahoy_meeting_notes_to_meeting`.
  const meeting = useMemo(() => {
    for (const assoc of object.associations ?? []) {
      if (assoc.association_type_name !== 'ahoy_meeting_notes_to_meeting') {
        continue;
      }
      const m = associatedObjects?.[assoc.target_entity_id];
      if (m) {
        return m;
      }
    }
    return undefined;
  }, [object.associations, associatedObjects]);

  const meetingTitle =
    meeting && getPropertyValue<string>(meeting.properties?.ahoy_title);
  const meetingStart =
    meeting && getPropertyValue<string>(meeting.properties?.ahoy_start_time);

  // Prefer the meeting's identity; fall back to the note's own title/created-at.
  const title =
    meetingTitle ||
    getPropertyValue<string>(object.properties?.ahoy_title) ||
    'Meeting notes';
  const timestamp =
    meetingStart ||
    getPropertyValue<string>(object.properties?.ahoy_created_at) ||
    undefined;
  const summary =
    getPropertyValue<string>(object.properties?.ahoy_summary) ?? '';
  const summaryPreview =
    summary.length > NOTE_SUMMARY_PREVIEW_CHAR_CAP
      ? summary.slice(0, NOTE_SUMMARY_PREVIEW_CHAR_CAP)
      : summary;
  const actionItems =
    getPropertyValue<string>(object.properties?.ahoy_action_items) ?? '';
  const keyTopics =
    getPropertyValue<string>(object.properties?.ahoy_key_topics) ?? '';
  const hasDetail = Boolean(summary || actionItems || keyTopics);

  // Link back to the originating meeting when we have it; otherwise the note.
  const targetId = meeting?.entity_id ?? object.entity_id;
  const handleOpen = useCallback(() => onOpen(targetId), [onOpen, targetId]);

  // A note with no expandable content is just a plain card linking to the
  // meeting — no accordion.
  if (!hasDetail) {
    return (
      <Button
        color='tertiary'
        noTextPadding
        onClick={handleOpen}
        className='w-full overflow-hidden !justify-start !py-3 !px-3 !rounded-[4px] border border-[var(--color-border-secondary)] hover:!bg-[var(--color-bg-secondary)]'
      >
        <div className='flex items-center gap-3 w-full min-w-0 text-left'>
          <span className='text-sm text-[var(--color-text-primary)] truncate min-w-0 flex-1 text-left'>
            {title}
          </span>
          {timestamp && (
            <span className='text-xs text-[var(--color-text-tertiary)] shrink-0 tabular-nums'>
              {formatUpcomingMeetingDateTime(timestamp, timeFormat)}
            </span>
          )}
        </div>
      </Button>
    );
  }

  // Card body links to the meeting; the accordion chevron expands the note's
  // full content inline (library accordion — matches CollapsibleSection).
  return (
    <AccordionItem className='w-full overflow-hidden rounded-[4px] border border-[var(--color-border-secondary)]'>
      {({ isExpanded }) => (
        <>
          {/* Header stays identical in every state: title (navigate) + date,
              with the accordion trigger fixed on the right. */}
          <div className='flex items-center'>
            <Button
              color='tertiary'
              noTextPadding
              onClick={handleOpen}
              className='min-w-0 flex-1 !justify-start !py-3 !pl-3 !pr-2 hover:!bg-[var(--color-bg-secondary)]'
            >
              <div className='flex items-center gap-3 w-full min-w-0 text-left'>
                <span className='text-sm text-[var(--color-text-primary)] truncate min-w-0 flex-1 text-left'>
                  {title}
                </span>
                {timestamp && (
                  <span className='text-xs text-[var(--color-text-tertiary)] shrink-0 tabular-nums'>
                    {formatUpcomingMeetingDateTime(timestamp, timeFormat)}
                  </span>
                )}
              </div>
            </Button>
            <AccordionTrigger
              aria-label={isExpanded ? 'Collapse notes' : 'Expand notes'}
              className='flex shrink-0 items-center self-stretch px-2 hover:bg-[var(--color-bg-secondary)]'
            >
              {/* Rotation is driven by this item's own `isExpanded` rather than
                  AccordionChevron's group-data selector: these note accordions
                  nest inside the section's AccordionItem (same `group/item`
                  name), so the group selector would bind to the parent's
                  expanded state instead of the note's. */}
              <ChevronDown
                aria-hidden='true'
                className={cx(
                  'w-4 h-4 shrink-0 text-fg-tertiary transition-transform duration-200 motion-reduce:transition-none',
                  isExpanded && 'rotate-180'
                )}
              />
            </AccordionTrigger>
          </div>
          {/* Collapsed preview — decorative, non-interactive. Summaries are AI-generated
              markdown, so they render through the same MarkdownContent the expanded
              NoteDetailBlock uses, with CollapsibleContent supplying the height clip and
              fade (a line-clamp cannot bound the sibling blocks rendered markdown emits).
              `inert` because this clip has no "Show more" escape: a link, an `ahoy:`
              button, or a table copy button rendered below the 60px fold would otherwise
              be an invisible tab stop, and focusing it scrolls the overflow-hidden box
              with no way back. The full summary stays one "Expand notes" click away. */}
          {summary && !isExpanded && (
            <div
              inert
              className='-mt-1 px-3 pb-3 text-xs text-[var(--color-text-tertiary)] text-left'
            >
              <CollapsibleContent
                maxCollapsedHeight={NOTE_SUMMARY_PREVIEW_MAX_HEIGHT_PX}
                showToggle={false}
              >
                <MarkdownContent
                  content={summaryPreview}
                  className={NOTE_SUMMARY_PREVIEW_MARKDOWN_CLASS}
                  size='compact'
                />
              </CollapsibleContent>
            </div>
          )}
          <AccordionPanel className='flex flex-col gap-3 border-t border-[var(--color-border-secondary)] px-3 py-3 text-left'>
            {summary && <NoteDetailBlock label='Summary' content={summary} />}
            {actionItems && (
              <NoteDetailBlock label='Action items' content={actionItems} />
            )}
            {keyTopics && (
              <NoteDetailBlock label='Key topics' content={keyTopics} />
            )}
            {/* Attendees — mounted (and fetched) only once the note is expanded. */}
            {isExpanded && meeting && (
              <MeetingAttendeesById
                meetingId={meeting.entity_id}
                orgId={object.org_id}
              />
            )}
          </AccordionPanel>
        </>
      )}
    </AccordionItem>
  );
}

/** A labelled markdown block shown inside an expanded meeting-notes card. */
function NoteDetailBlock({
  label,
  content,
}: {
  label: string;
  content: string;
}) {
  return (
    <div className='flex flex-col gap-1'>
      <span className='text-xs font-medium text-[var(--color-text-secondary)]'>
        {label}
      </span>
      <div className='text-xs text-[var(--color-text-primary)]'>
        <MarkdownContent content={content} size='compact' />
      </div>
    </div>
  );
}

/**
 * Risk row: warning icon + title + description + severity/status/category
 * tags (via `RiskCardContent`). Click opens the risk in the slideout stack.
 */
function RiskCardImpl({ object, onOpen }: EntityCardProps) {
  const entityId = object.entity_id;
  const title =
    getPropertyValue<string>(object.properties?.ahoy_title) || 'Risk Signal';
  const description =
    getPropertyValue<string>(object.properties?.ahoy_description) ?? undefined;
  const severity =
    getPropertyValue<string>(object.properties?.ahoy_severity) ?? undefined;
  const status =
    getPropertyValue<string>(object.properties?.ahoy_status) ?? undefined;
  const category =
    getPropertyValue<string>(object.properties?.ahoy_risk_category) ??
    undefined;
  const handleClick = useCallback(() => onOpen(entityId), [onOpen, entityId]);
  return (
    <div className='w-full overflow-hidden rounded-[4px] border border-[var(--color-border-secondary)] p-3'>
      <RiskCardContent
        title={title}
        description={description}
        severity={severity}
        status={status}
        category={category}
        onClick={handleClick}
      />
    </div>
  );
}

export const MeetingCard = memo(MeetingCardImpl);
export const MeetingDetailCard = memo(MeetingDetailCardImpl);
export const MeetingNotesCard = memo(MeetingNotesCardImpl);
export const RiskCard = memo(RiskCardImpl);
